import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { RbacProvider } from "@/lib/rbac";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import IosInstallBanner from "@/components/IosInstallBanner";
import AndroidInstallChip from "@/components/AndroidInstallChip";
import SafeBoundary from "@/components/SafeBoundary";
import { installUppercaseInputs } from "@/lib/uppercaseInputs";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import LiveProduction from "@/pages/LiveProduction";
import SalesOrders from "@/pages/SalesOrders";
import ProductionPlan from "@/pages/ProductionPlan";
import ProductionEntry from "@/pages/ProductionEntry";
import WeavingConsumption from "@/pages/WeavingConsumption";
import QARunningWeight from "@/pages/QARunningWeight";
import QAWeightRegister from "@/pages/QAWeightRegister";
import DepartmentProductionEntry from "@/pages/DepartmentProductionEntry";
import { CoveringSpandexEntry, FinishingEntry, DyeingEntry, FinishingDyeingLegacyRedirect, DepartmentEntryLegacyRedirect } from "@/pages/production/ProductionEntryShells";
import MaterialTransfer from "@/pages/MaterialTransfer";
import ProductionEntryRegister, { REGISTER_CONFIGS } from "@/pages/ProductionEntryRegister";
import FinishingProductionEntryRegister from "@/pages/FinishingProductionEntryRegister";
import CheckingProductionEntry from "@/pages/CheckingProductionEntry";
import CheckingProductionEntryRegister from "@/pages/CheckingProductionEntryRegister";
import PreparatoryEntry from "@/pages/PreparatoryEntryRetired";
import RMWastageSummary from "@/pages/RMWastageSummary";
import FGStockRegister from "@/pages/FGStockRegister";
import FGStockBook from "@/pages/FGStockBook";
import ManagementApprovals from "@/pages/ManagementApprovals";
import Downtime from "@/pages/Downtime";
import Quality from "@/pages/Quality";
import Reports from "@/pages/Reports";
import WarpingReconciliation from "@/pages/WarpingReconciliation";
import CoveringProductionReconciliation from "@/pages/CoveringProductionReconciliation";
import CoveringRawMaterialReconciliation from "@/pages/CoveringRawMaterialReconciliation";
import WeavingProductionReconciliation from "@/pages/WeavingProductionReconciliation";
import WeavingRawMaterialReconciliation from "@/pages/WeavingRawMaterialReconciliation";
import FinishingProductionReconciliation from "@/pages/FinishingProductionReconciliation";
import DyeingProductionReconciliation from "@/pages/DyeingProductionReconciliation";
import AIAssistant from "@/pages/AIAssistant";
import Masters from "@/pages/Masters";
import ArticleMaster from "@/pages/ArticleMaster";
import RawMaterialMaster from "@/pages/RawMaterialMaster";
import BOMMaster from "@/pages/BOMMaster";
import SFMaster from "@/pages/SFMaster";
import MaterialTraceability from "@/pages/MaterialTraceability";
import FGWarehouseReceipt from "@/pages/FGWarehouseReceipt";
import MRP from "@/pages/MRP";
import PurchaseRequirements from "@/pages/PurchaseRequirements";
import SupplierPriceList from "@/pages/SupplierPriceList";
import RmAlternateApprovals from "@/pages/RmAlternateApprovals";
import StockRegister from "@/pages/StockRegister";
import QCApproval from "@/pages/QCApproval";
import LowStockAlerts from "@/pages/LowStockAlerts";
import PurchaseReceipt from "@/pages/PurchaseReceipt";
import PurchaseOrders from "@/pages/PurchaseOrders";
import AuditLog from "@/pages/AuditLog";
import FxRateMaster from "@/pages/FxRateMaster";
import AccountingYearMaster from "@/pages/AccountingYearMaster";
import HolidayCalendar from "@/pages/HolidayCalendar";
import PackingSlipBackfillReport from "@/pages/PackingSlipBackfillReport";
import StockBook from "@/pages/StockBook";
import V2Inventory from "@/pages/V2Inventory";
import FGDispatchReconciliation from "@/pages/FGDispatchReconciliation";
import Maintenance from "@/pages/Maintenance";
import LiveDataCleanup from "@/pages/LiveDataCleanup";
import FgBackfill from "@/pages/FgBackfill";
import Inventory from "@/pages/Inventory";
import Exports from "@/pages/Exports";
import Rejections from "@/pages/Rejections";
import ProductionOrders from "@/pages/ProductionOrders";
import ChildWorkOrders from "@/pages/ChildWorkOrders";
import MaterialRequisitions from "@/pages/MaterialRequisitions";
import GodownBalances from "@/pages/GodownBalances";
import StageTransfer from "@/pages/StageTransfer";
import MaterialIssue from "@/pages/MaterialIssue";
import MaterialIssueMI from "@/pages/MaterialIssueMI";
import RolesPermissions from "@/pages/RolesPermissions";
import PermissionReferenceGuide from "@/pages/PermissionReferenceGuide";
import RoleMaster from "@/pages/RoleMaster";
import Feedback from "@/pages/Feedback";
import Catalogue from "@/pages/Catalogue";
import SalesDashboard from "@/pages/SalesDashboard";
import PurchaseDashboard from "@/pages/PurchaseDashboard";
import InventoryDashboard from "@/pages/InventoryDashboard";
import { MobileGuard } from "@/lib/mobile.jsx";
import PendingStatement from "@/pages/PendingStatement";
import PendingPurchaseStatement from "@/pages/PendingPurchaseStatement";
import PendingWorkOrder from "@/pages/PendingWorkOrder";
import AdminSettings from "@/pages/AdminSettings";
import ReversalAudit from "@/pages/ReversalAudit";
import SalesOrderReconciliation from "@/pages/SalesOrderReconciliation";
import DomesticSalesOrderReconciliation from "@/pages/DomesticSalesOrderReconciliation";
import ExportSalesOrderReconciliation from "@/pages/ExportSalesOrderReconciliation";
import ExportOrderReconciliation from "@/pages/ExportOrderReconciliation";
import ConsolidatedWoReconciliation from "@/pages/ConsolidatedWoReconciliation";
import DispatchHistory from "@/pages/DispatchHistory";
import ExportDispatchHistory from "@/pages/ExportDispatchHistory";
import TncTemplates from "@/pages/TncTemplates";
import BinTransfer from "@/pages/BinTransfer";
import CoveringRMTransfer from "@/pages/CoveringRMTransfer";
import ScrapEntry from "@/pages/ScrapEntry";
import StockReservation from "@/pages/StockReservation";
import PhysicalVerification from "@/pages/PhysicalVerification";
import OpeningStockEntry from "@/pages/OpeningStockEntry";
import PendingReturnableChallans from "@/pages/PendingReturnableChallans";
import PurchaseReturns from "@/pages/PurchaseReturns";
import ExcessInventory from "@/pages/ExcessInventory";
import ExcessProduction from "@/pages/ExcessProduction";
import StockTraceability from "@/pages/StockTraceability";
import GstInvoices from "@/pages/GstInvoices";
import ExportGstInvoices from "@/pages/ExportGstInvoices";
import ProformaInvoices from "@/pages/ProformaInvoices";
import TallySync from "@/pages/TallySync";
import api from "@/lib/api";
import { setPrintCompany } from "@/config/company";

// V28-003 · Controlled authenticated state for a user whose role has zero
// authorized navigable modules. Rendered inside the Layout shell so the top-bar
// Sign out stays available. Grants no access and never redirects.
function NoModules() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="no-access-state">
      <div className="max-w-md text-center">
        <div className="text-4xl mb-3">&#128274;</div>
        <h1 className="text-xl font-semibold text-white mb-2">No modules assigned</h1>
        <p className="text-sm text-zinc-400">
          No application modules are assigned to your role. Please contact your administrator.
        </p>
        <p className="text-xs text-zinc-500 mt-3">Use <span className="font-semibold">Sign out</span> in the top bar to exit.</p>
      </div>
    </div>
  );
}

// V28-003A · Shell-level guard is now AUTH-ONLY: it never replaces the Layout
// with a denial screen. Per-route permission checks run inside Layout via
// <RouteGuard> around the Outlet, so AccessDenied renders in the content area
// with the sidebar + Sign out intact. Unauthenticated → login.
function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  // Install once at app boot — global uppercase normaliser for every alpha
  // text input across masters, orders, planning, etc. Remarks/description
  // /email/password/search boxes are excluded inside the helper. Feb 2026.
  useEffect(() => { installUppercaseInputs(); }, []);
  // Feb 2026 · Bootstrap the print-header company profile once at boot.
  // Falls back to bundled defaults if the tenant hasn't saved one yet.
  useEffect(() => {
    (async () => {
      try {
        // Feb 2026 · Security Audit — boot fetch uses the /branding
        // subset (name / logo / address only). The full profile with
        // banking + GSTIN + PAN is Administrator-only via /company-profile.
        const { data } = await api.get("/company-profile/branding");
        if (data && (data.name || data.address)) setPrintCompany(data);
      } catch (_e) { /* silent — defaults kick in */ }
    })();
  }, []);
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <RbacProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Feb 2026 · Public Digital Catalogue — no auth wrapper so
                  Share links work for external customers. Deep-link form
                  /catalogue/:articleId opens a specific article dialog. */}
              <Route path="/catalogue" element={<Catalogue />} />
              <Route path="/catalogue/:articleId" element={<Catalogue />} />
              <Route
                path="/"
                element={
                  <Protected>
                    <MobileGuard>
                      <Layout />
                    </MobileGuard>
                  </Protected>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="no-access" element={<NoModules />} />
                <Route path="live" element={<LiveProduction />} />
                <Route path="sales-orders" element={<SalesOrders />} />
                <Route path="gst-invoices" element={<GstInvoices />} />
                <Route path="tally-sync" element={<TallySync />} />
                <Route path="export-gst-invoices" element={<ExportGstInvoices />} />
                <Route path="proforma-invoices" element={<ProformaInvoices piType="domestic" />} />
                <Route path="export-proforma-invoices" element={<ProformaInvoices piType="export" />} />
                <Route path="sales-dashboard" element={<SalesDashboard />} />
                <Route path="dashboards/purchase" element={<PurchaseDashboard />} />
                <Route path="dashboards/inventory" element={<InventoryDashboard />} />
                {/* Feb 2026 · Legacy /m mobile view redirected to Sales
                    Dashboard (new mobile landing per PWA start_url). */}
                <Route path="m" element={<Navigate to="/sales-dashboard" replace />} />
                <Route path="planning" element={<ProductionPlan />} />
                <Route path="production-orders" element={<ProductionOrders />} />
                <Route path="child-work-orders" element={<ChildWorkOrders />} />
                <Route path="material-requisitions" element={<MaterialRequisitions />} />
                <Route path="godown-balances" element={<GodownBalances />} />
                <Route path="stage-transfer" element={<StageTransfer />} />
                <Route path="material-issue" element={<MaterialIssue />} />
                <Route path="material-issue/mi" element={<MaterialIssueMI />} />
                <Route path="entry" element={<ProductionEntry />} />
                <Route path="production/weaving-consumption" element={<WeavingConsumption />} />
                {/* P17-UAT-PRODUCTION-UI-02 · dedicated per-process entry screens */}
                <Route path="production/warping-entry" element={<DepartmentProductionEntry lockedDept="WARPING" />} />
                <Route path="production/covering-spandex-entry" element={<CoveringSpandexEntry />} />
                <Route path="production/finishing-entry" element={<FinishingEntry />} />
                <Route path="production/dyeing-entry" element={<DyeingEntry />} />
                <Route path="production/finishing-dyeing-entry" element={<FinishingDyeingLegacyRedirect />} />
                <Route path="production/loom-entry" element={<Navigate to="/entry" replace />} />
                {/* legacy generic route → compatibility redirect (no second editable path) */}
                <Route path="production/department-entry" element={<DepartmentEntryLegacyRedirect />} />
                <Route path="production/material-transfer" element={<MaterialTransfer />} />
                <Route path="reports/warping-production-register" element={<ProductionEntryRegister config={REGISTER_CONFIGS.WARPING} />} />
                <Route path="reports/covering-production-register" element={<ProductionEntryRegister config={REGISTER_CONFIGS.COVERING} />} />
                <Route path="reports/spandex-warping-production-register" element={<ProductionEntryRegister config={REGISTER_CONFIGS.SPANDEX_WARPING} />} />
                <Route path="reports/finishing-production-register" element={<FinishingProductionEntryRegister />} />
                <Route path="production/checking-entry" element={<CheckingProductionEntry />} />
                <Route path="production/checking-entry/:id" element={<CheckingProductionEntry />} />
                <Route path="reports/checking-production-register" element={<CheckingProductionEntryRegister />} />
                <Route path="preparatory-entry" element={<PreparatoryEntry />} />
                <Route path="reports/rm-wastage-summary" element={<RMWastageSummary />} />
                <Route path="reports/warping-reconciliation" element={<WarpingReconciliation />} />
                <Route path="reports/covering-production-reconciliation" element={<CoveringProductionReconciliation />} />
                <Route path="reports/covering-rm-reconciliation" element={<CoveringRawMaterialReconciliation />} />
                <Route path="reports/weaving-production-reconciliation" element={<WeavingProductionReconciliation />} />
                <Route path="reports/weaving-rm-reconciliation" element={<WeavingRawMaterialReconciliation />} />
                <Route path="reports/wo-consolidated-reconciliation" element={<ConsolidatedWoReconciliation />} />
                <Route path="reports/finishing-production-reconciliation" element={<FinishingProductionReconciliation />} />
                <Route path="reports/dyeing-production-reconciliation" element={<DyeingProductionReconciliation />} />
                <Route path="sales/fg-stock-register" element={<FGStockRegister />} />
                <Route path="sales/fg-stock-book" element={<FGStockBook />} />
                <Route path="management-approvals" element={<ManagementApprovals />} />
                <Route path="downtime" element={<Downtime />} />
                <Route path="maintenance" element={<Maintenance />} />
                <Route path="admin/maintenance/live-data-cleanup" element={<LiveDataCleanup />} />
                <Route path="admin/maintenance/fg-backfill" element={<FgBackfill />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="quality" element={<Quality />} />
                <Route path="rejections" element={<Rejections />} />
                <Route path="exports" element={<Exports />} />
                <Route path="reports" element={<Reports />} />
                <Route path="reports/pending-sales-order" element={<PendingStatement />} />
                {/* Feb 2026 · Sister report — every WO with pending qty > 0 */}
                <Route path="reports/pending-work-order" element={<PendingWorkOrder />} />
                {/* Mar 03, 2026 · Sister report to Pending Sales Order. */}
                <Route path="reports/pending-purchase-order" element={<PendingPurchaseStatement />} />
                {/* Feb 2026 · Backwards-compat redirect — the report was
                    originally shipped as `pending-statement`. Renamed on
                    request to keep the URL slug uniform with the on-screen
                    title "Pending Sales Order". */}
                <Route path="reports/pending-statement" element={<Navigate to="/reports/pending-sales-order" replace />} />
                <Route path="ai" element={<AIAssistant />} />
                <Route path="masters/:tab" element={<Masters />} />
                <Route path="articles" element={<ArticleMaster />} />
                <Route path="raw-materials" element={<RawMaterialMaster />} />
                <Route path="bom" element={<BOMMaster />} />
                <Route path="sf-master" element={<SFMaster />} />
                <Route path="material-traceability" element={<MaterialTraceability />} />
                <Route path="inventory/fg-warehouse-receipt" element={<FGWarehouseReceipt />} />
                <Route path="mrp" element={<MRP />} />
                <Route path="purchase-requirements" element={<PurchaseRequirements />} />
                <Route path="masters/supplier-price-list" element={<SupplierPriceList />} />
                <Route path="masters/rm-alternate-approvals" element={<RmAlternateApprovals />} />
                <Route path="stock-register" element={<StockRegister />} />
                <Route path="qc-approval" element={<QCApproval />} />
                <Route path="qa/running-weight" element={<QARunningWeight />} />
                <Route path="qa/running-weight-register" element={<QAWeightRegister />} />
                <Route path="inventory/low-stock" element={<LowStockAlerts />} />
                <Route path="inventory/purchase-receipt" element={<PurchaseReceipt />} />
                <Route path="purchase-orders" element={<PurchaseOrders />} />
                <Route path="audit-log" element={<AuditLog />} />
                <Route path="masters/fx-rates" element={<FxRateMaster />} />
                <Route path="masters/accounting-year" element={<AccountingYearMaster />} />
                <Route path="settings/holiday-calendar" element={<HolidayCalendar />} />
                <Route path="reports/packing-slip-backfill" element={<PackingSlipBackfillReport />} />
                <Route path="inventory/stock-book" element={<StockBook />} />
                <Route path="inventory/v2" element={<V2Inventory />} />
                <Route path="inventory/fg-reconciliation" element={<FGDispatchReconciliation />} />

                <Route path="inventory/bin-transfer" element={<BinTransfer />} />
                <Route path="inventory/covering-rm-transfer" element={<CoveringRMTransfer />} />
                <Route path="inventory/scrap" element={<ScrapEntry />} />
                <Route path="inventory/reservations" element={<StockReservation />} />
                <Route path="inventory/physical-verification" element={<PhysicalVerification />} />
                <Route path="inventory/opening-stock" element={<OpeningStockEntry />} />
                <Route path="inventory/returnable-challans" element={<PendingReturnableChallans />} />
                <Route path="inventory/purchase-returns" element={<PurchaseReturns />} />
                <Route path="inventory/excess" element={<ExcessInventory />} />
                <Route path="dispatch/excess-production" element={<ExcessProduction />} />
                <Route path="quality/traceability" element={<StockTraceability />} />
                <Route path="masters" element={<Navigate to="/masters/machines" replace />} />
                <Route path="settings/roles" element={<RolesPermissions />} />
                <Route path="reports/permission-reference" element={<PermissionReferenceGuide />} />
                <Route path="masters/roles" element={<RoleMaster />} />
                <Route path="settings/company" element={<AdminSettings />} />
                <Route path="reports/reversal-audit" element={<ReversalAudit />} />
                <Route path="reports/so-reconciliation" element={<SalesOrderReconciliation />} />
                <Route path="reports/domestic-so-reconciliation" element={<DomesticSalesOrderReconciliation />} />
                <Route path="reports/eso-reconciliation" element={<ExportOrderReconciliation />} />
                <Route path="reports/export-so-reconciliation" element={<ExportSalesOrderReconciliation />} />
                <Route path="reports/dispatch-history" element={<DispatchHistory defaultSource="SO" lockSource />} />
                <Route path="reports/eso-dispatch-history" element={<ExportDispatchHistory />} />
                <Route path="settings/tnc-templates" element={<TncTemplates />} />
                <Route path="feedback" element={<Feedback />} />
              </Route>
            </Routes>
          <Toaster position="top-right" richColors />
          <SafeBoundary><IosInstallBanner /></SafeBoundary>
          <SafeBoundary><AndroidInstallChip /></SafeBoundary>
          </RbacProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
